import { BackendDatetime, UIDate } from '../../utils/datetimeUtils'
import { CatalogueReportDTO, RightReportDTO } from '../../apis/reports/dto'
import {
  DEFAULT_CACHE_TIME_MS,
  QUERY_CUSTOMER_GET_ALL
} from '../../apis/moovyQueryKeys'
import { FormControl, Grid, MenuItem, Radio, RadioGroup } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  MoovyButton,
  MoovyContainer,
  MoovyContainerContent,
  MoovyContainerHeader,
  MoovyForm,
  MoovyMutationError,
  MoovyQueryHandler,
  MoovyScrollToTop
} from '../../components'
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import CustomersAPI from '../../apis/customers/customersAPI'
import { DateRangePicker } from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'
import { MoovyToast } from '../../utils/moovyToast'
import { ReportsAPI } from '../../apis/reports/reportsAPI'
import { assertExhaustive } from '../../utils/utils'
import jsonexport from 'jsonexport'
import useAuth from '../../hooks/useAuth'
import useLocale from '../../hooks/useLocale'

const Reports = () => {
  const intl = useIntl()
  const { locale } = useLocale()
  const { isPermission } = useAuth()
  type ReportType = 'LANDLORD' | 'TENANT'

  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [reportType, setReportType] = useState<ReportType>(
    isPermission('READ', 'CATALOGUE_REPORTS') ? 'LANDLORD' : 'TENANT'
  )
  const initialDateTime = DateTime.local().setLocale(locale || 'fi')
  const [givenStartDate, setGivenStartDate] = useState<DateTime>(
    initialDateTime.startOf('day')
  )
  const [givenEndDate, setGivenEndDate] = useState<DateTime>(
    initialDateTime.endOf('day')
  )

  const queryCustomers = useQuery({
    queryKey: [QUERY_CUSTOMER_GET_ALL],
    // TODO Antti: Should be fixed later because there might be more than 500 customers.
    queryFn: () =>
      CustomersAPI.getCustomers(false, {
        limit: 500,
        sort: 'TYPE',
        direction: 'ASC'
      }),
    staleTime: DEFAULT_CACHE_TIME_MS
  })

  const customers = queryCustomers.data?.data || []
  const customerOptions = customers.map((item) => {
    return { key: item.id, value: item.name }
  })

  if (customerOptions.length > 1) {
    customerOptions.unshift({
      key: 'ALL',
      value: intl.formatMessage({ id: 'page.reports.customer.all' })
    })
  }

  const customerIds =
    selectedCustomerId === 'ALL'
      ? customerOptions.slice(1).map((item) => item.key)
      : [selectedCustomerId]

  const successHandler = async (data: CatalogueReportDTO | RightReportDTO) => {
    if (!data || data.data.length == 0) {
      MoovyToast.showToast(
        <FormattedMessage id="page.reports.toast.error.emptyReport" />
      )
      return
    }
    try {
      const result = await generateCSVReport(data)
      generateCSVFile(data.reportName, result)
    } catch (e) {
      MoovyToast.showToast(
        <FormattedMessage id="page.reports.toast.error.csvFailed" />,
        false
      )
    }
  }

  const cataloguesMutation = useMutation({
    mutationFn: () =>
      ReportsAPI.getCataloguesReport(
        customerIds,
        BackendDatetime.toUTC(givenStartDate),
        BackendDatetime.toUTC(givenEndDate)
      ),
    onSuccess: (response) => successHandler(response.data)
  })

  const rightsMutation = useMutation({
    mutationFn: () =>
      ReportsAPI.getRightsReport(
        customerIds,
        BackendDatetime.toUTC(givenStartDate),
        BackendDatetime.toUTC(givenEndDate)
      ),
    onSuccess: (response) => successHandler(response.data)
  })

  useEffect(() => {
    if (
      queryCustomers.isSuccess &&
      !selectedCustomerId &&
      customerOptions.length > 0
    ) {
      setSelectedCustomerId(customerOptions[0].key)
    }
  }, [queryCustomers.isSuccess])

  const generateCSVReport = (
    reportResponse: CatalogueReportDTO | RightReportDTO
  ) => {
    return new Promise((resolve, reject) => {
      jsonexport(reportResponse.data, (err, csv) => {
        if (err) {
          return reject(new Error('CSV generation failed'))
        }
        resolve(csv)
      })
    })
  }

  const generateCSVFile = (reportName: string, result: any) => {
    const startDate = givenStartDate
      ? UIDate.toCustomString(givenStartDate)
      : ''
    const endDate = givenEndDate ? UIDate.toCustomString(givenEndDate) : ''
    const filename = `${reportName}-${startDate}-${endDate}`

    const blob = new Blob([result], { type: 'data:text/csv;charset=utf-8,' })
    const blobURL = window.URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.download = `${filename}.csv`
    anchor.href = blobURL
    anchor.dataset.downloadurl = [
      'text/csv',
      anchor.download,
      anchor.href
    ].join(':')
    anchor.click()

    // Remove URL.createObjectURL. The browser should not save the reference to the file.
    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      URL.revokeObjectURL(blobURL)
    }, 100)
  }

  const requestReport = () => {
    if (!givenStartDate && !givenEndDate && !selectedCustomerId) {
      return
    }

    switch (reportType) {
      case 'LANDLORD':
        cataloguesMutation.mutate()
        break
      case 'TENANT':
        rightsMutation.mutate()
        break
      default:
        assertExhaustive(reportType)
    }
  }

  return (
    <MoovyQueryHandler query={queryCustomers} rootPageIfNotFoundError>
      <MoovyScrollToTop />
      <MoovyContainer>
        <MoovyContainerHeader>
          <FormattedMessage id="page.reports.title" />
        </MoovyContainerHeader>
        <MoovyContainerContent>
          <form>
            <Grid container>
              <Grid item md={6}>
                <MoovyForm.FormContainer>
                  <MoovyForm.FormBoxRow>
                    <MoovyMutationError
                      mutation={cataloguesMutation}
                      errorTitleKey="common.actions.get.error"
                    />
                    <MoovyMutationError
                      mutation={rightsMutation}
                      errorTitleKey="common.actions.get.error"
                    />
                  </MoovyForm.FormBoxRow>
                  {(isPermission('READ', 'CATALOGUE_REPORTS') ||
                    isPermission('READ', 'RIGHT_REPORTS')) && (
                    <MoovyForm.FormBoxRow>
                      <RadioGroup
                        value={reportType}
                        onChange={(event) =>
                          setReportType(event.target.value as ReportType)
                        }
                      >
                        <MoovyForm.FormLabel
                          sx={{ fontWeight: 'bold' }}
                          focused={false}
                        >
                          <FormattedMessage id="page.reports.type.radioGroup.title" />
                        </MoovyForm.FormLabel>
                        {isPermission('READ', 'CATALOGUE_REPORTS') && (
                          <MoovyForm.FormRadio
                            control={<Radio />}
                            label={
                              <FormattedMessage id="page.reports.type.radioGroup.landlord" />
                            }
                            value={'LANDLORD'}
                          />
                        )}
                        <MoovyForm.FormRadio
                          control={<Radio />}
                          label={
                            <FormattedMessage id="page.reports.type.radioGroup.tenant" />
                          }
                          value={'TENANT'}
                        />
                      </RadioGroup>
                    </MoovyForm.FormBoxRow>
                  )}
                  <MoovyForm.FormLabel
                    sx={{ fontWeight: 'bold' }}
                    focused={false}
                  >
                    <FormattedMessage id="page.reports.customer.title" />
                  </MoovyForm.FormLabel>
                  <MoovyForm.FormBoxRow>
                    <MoovyForm.FormSelect
                      name="customerId"
                      label={intl.formatMessage({
                        id: 'page.reports.customer.label'
                      })}
                      value={selectedCustomerId}
                      onChange={(event) =>
                        setSelectedCustomerId(
                          (event?.target?.value as string) || ''
                        )
                      }
                    >
                      {customerOptions.map((item) => {
                        return (
                          <MenuItem key={item.key} value={item.key}>
                            {item.value}
                          </MenuItem>
                        )
                      })}
                    </MoovyForm.FormSelect>
                  </MoovyForm.FormBoxRow>
                  <MoovyForm.FormLabel
                    sx={{ fontWeight: 'bold' }}
                    focused={false}
                  >
                    <FormattedMessage id="page.reports.range.title" />
                  </MoovyForm.FormLabel>
                  <MoovyForm.FormBoxRow>
                    <FormControl fullWidth>
                      <DateRangePicker
                        localeText={{
                          start: intl.formatMessage({
                            id: 'page.reports.daterange.start'
                          }),
                          end: intl.formatMessage({
                            id: 'page.reports.daterange.end'
                          }),
                          toolbarTitle: intl.formatMessage({
                            id: 'page.reports.range.title'
                          }),
                          cancelButtonLabel: intl.formatMessage({
                            id: 'common.buttons.cancel'
                          }),
                          clearButtonLabel: intl.formatMessage({
                            id: 'common.buttons.clear'
                          }),
                          okButtonLabel: intl.formatMessage({
                            id: 'common.ok'
                          })
                        }}
                        value={[givenStartDate, givenEndDate]}
                        displayWeekNumber
                        onAccept={(values) => {
                          const startDate = values[0]
                          const endDate = values[1]
                          setGivenStartDate(
                            startDate || initialDateTime.startOf('day')
                          )
                          setGivenEndDate(
                            endDate || initialDateTime.endOf('day')
                          )
                        }}
                      />
                    </FormControl>
                  </MoovyForm.FormBoxRow>
                  <MoovyForm.FormBoxRow>
                    {(isPermission('READ', 'CATALOGUE_REPORTS') ||
                      isPermission('READ', 'RIGHT_REPORTS')) && (
                      <MoovyButton
                        onClick={requestReport}
                        mutation={
                          reportType === 'LANDLORD'
                            ? cataloguesMutation
                            : rightsMutation
                        }
                      >
                        <FormattedMessage id="page.reports.submit.button" />
                      </MoovyButton>
                    )}
                  </MoovyForm.FormBoxRow>
                </MoovyForm.FormContainer>
              </Grid>
            </Grid>
          </form>
        </MoovyContainerContent>
      </MoovyContainer>
    </MoovyQueryHandler>
  )
}

export default Reports
